import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPackages, getCarriers, getSettings, refreshAllPackages, createPackage, createBulkPackages, deletePackage, updatePackage, archivePackage, refreshPackage, updateSettings, testConnection, changePassword, getNextCheckTime } from '../lib/api';
import { formatDate, formatStatus, getStatusClass, getDirectionClass } from '../lib/utils';
import { getCarrierColor, getCarrierInitials } from '../lib/carrierLogos';
import { useTheme } from '../contexts/ThemeContext';
import { requestNotificationPermission, sendDeliveryNotification, checkNotificationSupport } from '../lib/notifications';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ScrollArea } from '../components/ui/scroll-area';
import { Package, Plus, RefreshCw, Settings, LogOut, Archive, Trash2, ArrowDownLeft, ArrowUpRight, Loader2, Clock, MapPin, Moon, Sun, Search, ArrowUpDown, Bell, Key, Zap, CheckCircle, XCircle, AlertTriangle, Terminal, X, Timer, Pencil, Volume2, VolumeX, Sparkles, Download, Copy } from 'lucide-react';
import { toast } from 'sonner';

// Chime sound for updates (base64 encoded short notification sound)
const CHIME_SOUND = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH+LkpGLgnh0eYSOl5mVjYN6dn6IkZiXkYZ8dnh+h46Ri4N5c3Z7g4qMiYJ5c3R4foWKi4eCeXRzeH6Fh4eBenRydHmAhIWCfnl0c3V5foKEgoB7dnR0eHt+gYKAfnp2dHV3en2AgH98enZ1dXd5fH5/fnx5dnV1d3l7fX59fHp3dXV2eHp8fX18e3l3dnZ3eXt8fHt6eHZ2dnd5e3x8e3p4d3Z2d3l6e3t7enh3dnZ3eHp7e3t6eXd2dnd4ent7enl4d3Z2d3h5ent6eXh3dnZ3eHl6enp5eHd2dnd4eXp6eXh3dnZ2d3h5enl5eHd2dnd3eHl5eXh4d3Z2dnd4eXl5eHd3dnZ3d3h5eXl4d3d2dnd3eHl5eHh3d3Z2d3d4eHl4eHd3dnZ3d3h4eHh4d3d2dnd3eHh4eHd3d3Z3d3d4eHh4d3d3dnd3d3h4eHd3d3d2d3d3eHh4d3d3d3Z3d3d3eHh3d3d3dnd3d3d4eHd3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3');

export default function DashboardPage() {
    const [packages, setPackages] = useState([]);
    const [carriers, setCarriers] = useState([]);
    const [settings, setSettings] = useState({ update_frequency: 1440, shippo_api_key: '', use_test_key: true, key_mode: null, refresh_delay: 10, chime_enabled: true });
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
    const [newPackage, setNewPackage] = useState({ tracking_number: '', carrier: 'usps', direction: 'inbound', description: '' });
    const [addingPackage, setAddingPackage] = useState(false);
    const [bulkMode, setBulkMode] = useState(false);
    const [bulkTrackingNumbers, setBulkTrackingNumbers] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('date_desc');
    const [notificationsEnabled, setNotificationsEnabled] = useState(false);
    const [newApiKey, setNewApiKey] = useState('');
    const [savingApiKey, setSavingApiKey] = useState(false);
    const [testingConnection, setTestingConnection] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState(null);
    const [debugConsoleOpen, setDebugConsoleOpen] = useState(false);
    const [debugLogs, setDebugLogs] = useState([]);
    const [togglingKeyMode, setTogglingKeyMode] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [changingPassword, setChangingPassword] = useState(false);
    const [countdown, setCountdown] = useState(null); // seconds until next check
    const [waitingForRefresh, setWaitingForRefresh] = useState(false); // wait after timer fires
    const [waitCountdown, setWaitCountdown] = useState(0); // countdown for the wait period
    const [updatedPackageIds, setUpdatedPackageIds] = useState(new Set()); // IDs of packages that were just updated
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingPackage, setEditingPackage] = useState(null);
    const [editForm, setEditForm] = useState({ tracking_number: '', direction: '', description: '' });
    const [savingEdit, setSavingEdit] = useState(false);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const previousPackagesRef = useRef([]);
    const countdownIntervalRef = useRef(null);
    const autoRefreshTimeoutRef = useRef(null);
    const navigate = useNavigate();
    const { theme, toggleTheme } = useTheme();

    // Play chime sound
    const playChime = useCallback(() => {
        if (settings.chime_enabled) {
            try {
                CHIME_SOUND.currentTime = 0;
                CHIME_SOUND.volume = 0.5;
                CHIME_SOUND.play().catch(() => {
                    // Ignore autoplay errors
                });
            } catch (e) {
                // Ignore errors
            }
        }
    }, [settings.chime_enabled]);

    const addDebugLog = (type, message, data = null) => {
        const log = {
            id: Date.now(),
            timestamp: new Date().toISOString(),
            type,
            message,
            data
        };
        setDebugLogs(prev => [log, ...prev].slice(0, 100)); // Keep last 100 logs
    };

    const fetchData = useCallback(async () => {
        try {
            const [pkgs, carr, sett] = await Promise.all([
                getPackages(false),
                getCarriers(),
                getSettings()
            ]);
            
            // Check for newly delivered packages and send notifications
            if (notificationsEnabled && previousPackagesRef.current.length > 0) {
                pkgs.forEach(pkg => {
                    const prevPkg = previousPackagesRef.current.find(p => p.id === pkg.id);
                    if (prevPkg && prevPkg.status !== 'DELIVERED' && pkg.status === 'DELIVERED') {
                        sendDeliveryNotification(pkg.tracking_number, pkg.carrier_name);
                    }
                });
            }
            
            previousPackagesRef.current = pkgs;
            setPackages(pkgs);
            setCarriers(carr);
            setSettings(sett);
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    }, [notificationsEnabled]);

    useEffect(() => {
        fetchData();
        
        // Check notification permission status
        if (checkNotificationSupport() && Notification.permission === 'granted') {
            setNotificationsEnabled(true);
        }
    }, [fetchData]);

    // Fetch next check time and start countdown
    const fetchNextCheckTime = useCallback(async () => {
        try {
            const data = await getNextCheckTime();
            const nextCheck = new Date(data.next_check_time);
            const now = new Date();
            const diffSeconds = Math.max(0, Math.floor((nextCheck - now) / 1000));
            setCountdown(diffSeconds);
            const hours = data.update_frequency / 60;
            addDebugLog('info', `Next auto-check scheduled in ${formatCountdown(diffSeconds)}`, {
                next_check_time: data.next_check_time,
                last_update: data.last_tracking_update,
                frequency: `${hours}h (${data.update_frequency} min)`
            });
        } catch (error) {
            console.error('Failed to fetch next check time:', error);
        }
    }, []);

    // Format countdown as MM:SS or HH:MM:SS
    const formatCountdown = (seconds) => {
        if (seconds === null || seconds === undefined) return '--:--';
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        if (hrs > 0) {
            return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Start countdown timer
    useEffect(() => {
        if (!loading) {
            fetchNextCheckTime();
        }
    }, [loading, fetchNextCheckTime]);

    // Countdown interval
    useEffect(() => {
        if (countdown === null) return;

        countdownIntervalRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev === null || prev <= 0) {
                    // Timer reached zero - trigger auto-refresh sequence
                    if (prev === 0 && !waitingForRefresh) {
                        const delay = settings.refresh_delay || 10;
                        addDebugLog('info', `⏰ Auto-check timer fired! Waiting ${delay} seconds for background job to complete...`);
                        setWaitingForRefresh(true);
                        setWaitCountdown(delay);
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
            }
        };
    }, [countdown, waitingForRefresh, settings.refresh_delay]);

    // Handle the automatic refresh after 90-second wait
    const handleAutoRefresh = useCallback(async () => {
        addDebugLog('info', '🔄 Auto-refreshing packages after background job...');
        
        try {
            const newPackages = await getPackages(false);
            const oldPackages = previousPackagesRef.current;
            
            // Find updated and new packages
            const changedIds = new Set();
            let updatedCount = 0;
            let newCount = 0;
            
            newPackages.forEach(newPkg => {
                const oldPkg = oldPackages.find(p => p.id === newPkg.id);
                if (!oldPkg) {
                    // New package
                    changedIds.add(newPkg.id);
                    newCount++;
                    addDebugLog('success', `➕ New package detected: ${newPkg.tracking_number}`, newPkg);
                } else if (
                    oldPkg.status !== newPkg.status ||
                    oldPkg.eta !== newPkg.eta ||
                    JSON.stringify(oldPkg.tracking_history) !== JSON.stringify(newPkg.tracking_history)
                ) {
                    // Updated package
                    changedIds.add(newPkg.id);
                    updatedCount++;
                    addDebugLog('success', `✓ Package updated: ${newPkg.tracking_number} (${oldPkg.status} → ${newPkg.status})`, {
                        old: { status: oldPkg.status, eta: oldPkg.eta },
                        new: { status: newPkg.status, eta: newPkg.eta }
                    });
                    
                    // Check for delivery notification
                    if (notificationsEnabled && oldPkg.status !== 'DELIVERED' && newPkg.status === 'DELIVERED') {
                        sendDeliveryNotification(newPkg.tracking_number, newPkg.carrier_name);
                    }
                }
            });
            
            // Update state
            setPackages(newPackages);
            previousPackagesRef.current = newPackages;
            
            // Mark packages for animation
            if (changedIds.size > 0) {
                setUpdatedPackageIds(changedIds);
                
                // Show toast notification
                let message = '';
                if (updatedCount > 0 && newCount > 0) {
                    message = `${updatedCount} package${updatedCount !== 1 ? 's' : ''} updated, ${newCount} new`;
                } else if (updatedCount > 0) {
                    message = `${updatedCount} package${updatedCount !== 1 ? 's' : ''} updated`;
                } else if (newCount > 0) {
                    message = `${newCount} new package${newCount !== 1 ? 's' : ''} detected`;
                }
                toast.success(message);
                
                // Play chime sound for updates
                playChime();
                
                addDebugLog('info', `📦 Auto-refresh complete: ${message}`, {
                    updated_count: updatedCount,
                    new_count: newCount,
                    changed_ids: Array.from(changedIds)
                });
                
                // Clear animation flags after animation completes
                setTimeout(() => {
                    setUpdatedPackageIds(new Set());
                }, 1500);
            } else {
                addDebugLog('info', '📦 Auto-refresh complete: No changes detected');
            }
            
            // Fetch new countdown time
            await fetchNextCheckTime();
            
        } catch (error) {
            addDebugLog('error', 'Auto-refresh failed', { error: error.message });
            toast.error('Auto-refresh failed');
        } finally {
            setWaitingForRefresh(false);
            setWaitCountdown(0);
        }
    }, [notificationsEnabled, fetchNextCheckTime, playChime]);

    // 90-second wait countdown after timer fires
    useEffect(() => {
        if (!waitingForRefresh) return;

        const waitInterval = setInterval(() => {
            setWaitCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(waitInterval);
                    // Trigger auto-refresh
                    handleAutoRefresh();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(waitInterval);
    }, [waitingForRefresh, handleAutoRefresh]);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('username');
        navigate('/login');
    };

    const handleRefreshAll = async () => {
        setRefreshing(true);
        addDebugLog('info', 'Starting refresh for all packages...');
        try {
            const result = await refreshAllPackages();
            addDebugLog('info', `Refresh completed: ${result.success_count}/${result.total} successful`, result);
            
            // Log individual package results
            if (result.debug_logs) {
                result.debug_logs.forEach(log => {
                    if (log.success) {
                        addDebugLog('success', `✓ ${log.tracking_number} (${log.carrier}): ${log.old_status} → ${log.new_status}`, log);
                    } else {
                        addDebugLog('error', `✗ ${log.tracking_number} (${log.carrier}): ${log.error}`, log);
                    }
                });
            }
            
            await fetchData();
            
            if (result.error_count > 0) {
                toast.warning(`Refreshed ${result.success_count}/${result.total} packages. ${result.error_count} failed - check debug console.`);
                setDebugConsoleOpen(true);
            } else {
                toast.success('All packages refreshed');
            }
        } catch (error) {
            addDebugLog('error', 'Refresh failed', { error: error.message });
            toast.error('Failed to refresh packages');
        } finally {
            setRefreshing(false);
            // Reset countdown after manual refresh
            fetchNextCheckTime();
        }
    };

    // Parse bulk import line - supports: tracking_number OR tracking_number, description
    // Description can contain commas, single quotes, and double quotes
    const parseBulkLine = (line) => {
        const trimmed = line.trim();
        if (!trimmed) return null;
        
        // Find the first comma that separates tracking number from description
        const firstCommaIndex = trimmed.indexOf(',');
        if (firstCommaIndex === -1) {
            // No comma - just tracking number
            return { tracking_number: trimmed, description: '' };
        }
        
        const tracking_number = trimmed.substring(0, firstCommaIndex).trim();
        const description = trimmed.substring(firstCommaIndex + 1).trim();
        
        return { tracking_number, description };
    };

    const handleAddPackage = async (e) => {
        e.preventDefault();
        
        if (bulkMode) {
            // Bulk mode - multiple tracking numbers with optional descriptions
            const lines = bulkTrackingNumbers.split('\n').filter(t => t.trim());
            const parsedItems = lines.map(parseBulkLine).filter(item => item && item.tracking_number);
            
            if (parsedItems.length === 0 || !newPackage.carrier) {
                toast.error('Please enter at least one tracking number and select a carrier');
                return;
            }

            setAddingPackage(true);
            try {
                const result = await createBulkPackages({
                    items: parsedItems,
                    carrier: newPackage.carrier,
                    direction: newPackage.direction
                });
                
                if (result.created_count > 0) {
                    await fetchData();
                    toast.success(`Added ${result.created_count} package${result.created_count > 1 ? 's' : ''}`);
                }
                if (result.error_count > 0) {
                    toast.error(`Failed to add ${result.error_count} package${result.error_count > 1 ? 's' : ''}`);
                }
                
                setBulkTrackingNumbers('');
                setNewPackage({ tracking_number: '', carrier: 'usps', direction: 'inbound', description: '' });
                setAddDialogOpen(false);
            } catch (error) {
                toast.error('Failed to add packages');
            } finally {
                setAddingPackage(false);
            }
        } else {
            // Single mode
            if (!newPackage.tracking_number || !newPackage.carrier) {
                toast.error('Please fill in all required fields');
                return;
            }

            setAddingPackage(true);
            try {
                const pkg = await createPackage(newPackage);
                setPackages([pkg, ...packages]);
                previousPackagesRef.current = [pkg, ...previousPackagesRef.current];
                setNewPackage({ tracking_number: '', carrier: 'usps', direction: 'inbound', description: '' });
                setAddDialogOpen(false);
                toast.success('Package added successfully');
            } catch (error) {
                toast.error('Failed to add package');
            } finally {
                setAddingPackage(false);
            }
        }
    };

    const handleDelete = async (id) => {
        try {
            await deletePackage(id);
            setPackages(packages.filter(p => p.id !== id));
            previousPackagesRef.current = previousPackagesRef.current.filter(p => p.id !== id);
            toast.success('Package deleted');
        } catch (error) {
            toast.error('Failed to delete package');
        }
    };

    const handleArchive = async (id) => {
        try {
            await archivePackage(id);
            setPackages(packages.filter(p => p.id !== id));
            previousPackagesRef.current = previousPackagesRef.current.filter(p => p.id !== id);
            toast.success('Package archived');
        } catch (error) {
            toast.error('Failed to archive package');
        }
    };

    const handleOpenEdit = (pkg) => {
        setEditingPackage(pkg);
        setEditForm({
            tracking_number: pkg.tracking_number,
            direction: pkg.direction,
            description: pkg.description || ''
        });
        setEditDialogOpen(true);
    };

    const handleSaveEdit = async () => {
        if (!editingPackage) return;
        
        setSavingEdit(true);
        try {
            const updateData = {};
            
            // Only include changed fields
            if (editForm.tracking_number !== editingPackage.tracking_number) {
                updateData.tracking_number = editForm.tracking_number;
            }
            if (editForm.direction !== editingPackage.direction) {
                updateData.direction = editForm.direction;
            }
            if (editForm.description !== (editingPackage.description || '')) {
                updateData.description = editForm.description;
            }
            
            if (Object.keys(updateData).length === 0) {
                toast.info('No changes to save');
                setEditDialogOpen(false);
                return;
            }
            
            const updatedPkg = await updatePackage(editingPackage.id, updateData);
            
            // Update packages list
            setPackages(packages.map(p => p.id === editingPackage.id ? updatedPkg : p));
            previousPackagesRef.current = previousPackagesRef.current.map(p => p.id === editingPackage.id ? updatedPkg : p);
            
            setEditDialogOpen(false);
            setEditingPackage(null);
            toast.success('Package updated successfully');
            
            // Play chime if tracking info was refreshed
            if (updateData.tracking_number) {
                playChime();
            }
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to update package';
            toast.error(message);
        } finally {
            setSavingEdit(false);
        }
    };

    const handleRefreshSingle = async (id) => {
        const pkg = packages.find(p => p.id === id);
        addDebugLog('info', `Refreshing ${pkg?.tracking_number || id}...`);
        try {
            const result = await refreshPackage(id);
            const debug = result.debug;
            const updated = result.package;
            
            // Log debug info
            if (debug) {
                if (debug.success) {
                    addDebugLog('success', `✓ ${debug.tracking_number} (${debug.carrier}): ${debug.old_status} → ${debug.new_status}`, debug);
                } else {
                    addDebugLog('error', `✗ ${debug.tracking_number} (${debug.carrier}): ${debug.error}`, debug);
                }
            }
            
            // Check if status changed to delivered
            const oldPkg = packages.find(p => p.id === id);
            if (notificationsEnabled && oldPkg && oldPkg.status !== 'DELIVERED' && updated.status === 'DELIVERED') {
                sendDeliveryNotification(updated.tracking_number, updated.carrier_name);
            }
            
            setPackages(packages.map(p => p.id === id ? updated : p));
            previousPackagesRef.current = previousPackagesRef.current.map(p => p.id === id ? updated : p);
            
            if (debug && !debug.success) {
                toast.warning('Refresh completed with errors - check debug console');
                setDebugConsoleOpen(true);
            } else {
                toast.success('Package refreshed');
            }
        } catch (error) {
            addDebugLog('error', `Failed to refresh package ${id}`, { error: error.message });
            toast.error('Failed to refresh package');
        }
    };

    const handleUpdateSettings = async (frequency) => {
        try {
            await updateSettings({ id: 'settings', update_frequency: frequency, shippo_api_key: settings.shippo_api_key });
            setSettings({ ...settings, update_frequency: frequency });
            const hours = frequency / 60;
            toast.success(`Update frequency set to ${hours} hour${hours !== 1 ? 's' : ''}`);
            // Refresh countdown timer to reflect new schedule
            await fetchNextCheckTime();
        } catch (error) {
            toast.error('Failed to update settings');
        }
    };

    const handleSaveApiKey = async () => {
        if (!newApiKey.trim()) {
            toast.error('Please enter an API key');
            return;
        }
        
        setSavingApiKey(true);
        try {
            const updated = await updateSettings({ 
                id: 'settings', 
                update_frequency: settings.update_frequency, 
                shippo_api_key: newApiKey,
                use_test_key: settings.use_test_key
            });
            setSettings(updated);
            setNewApiKey('');
            setConnectionStatus(null);
            toast.success('Shippo API key saved');
        } catch (error) {
            toast.error('Failed to update API key');
        } finally {
            setSavingApiKey(false);
        }
    };

    const handleToggleKeyMode = async (useTestKey) => {
        // If trying to use live key but none is saved
        if (!useTestKey && !settings.key_mode?.has_live_key) {
            toast.error('Please save a live API key first');
            return;
        }
        
        setTogglingKeyMode(true);
        try {
            const updated = await updateSettings({ 
                id: 'settings', 
                update_frequency: settings.update_frequency, 
                shippo_api_key: settings.shippo_api_key,
                use_test_key: useTestKey
            });
            setSettings(updated);
            addDebugLog('info', `Switched to ${useTestKey ? 'TEST' : 'LIVE'} API key`);
            toast.success(`Now using ${useTestKey ? 'test' : 'live'} API key`);
        } catch (error) {
            toast.error('Failed to switch key mode');
        } finally {
            setTogglingKeyMode(false);
        }
    };

    const handleChangePassword = async () => {
        if (!currentPassword || !newPassword || !confirmPassword) {
            toast.error('Please fill in all password fields');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('New passwords do not match');
            return;
        }
        if (newPassword.length < 6) {
            toast.error('New password must be at least 6 characters');
            return;
        }

        setChangingPassword(true);
        try {
            await changePassword(currentPassword, newPassword);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            toast.success('Password changed successfully');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to change password');
        } finally {
            setChangingPassword(false);
        }
    };

    const handleTestConnection = async () => {
        setTestingConnection(true);
        setConnectionStatus(null);
        try {
            const result = await testConnection(newApiKey || null);
            setConnectionStatus(result);
            if (result.success) {
                toast.success('API connection successful');
            } else {
                toast.error(result.message);
            }
        } catch (error) {
            setConnectionStatus({ success: false, message: 'Connection test failed' });
            toast.error('Connection test failed');
        } finally {
            setTestingConnection(false);
        }
    };

    const handleToggleNotifications = async () => {
        if (!notificationsEnabled) {
            const granted = await requestNotificationPermission();
            if (granted) {
                setNotificationsEnabled(true);
                toast.success('Delivery notifications enabled');
            } else {
                toast.error('Notification permission denied');
            }
        } else {
            setNotificationsEnabled(false);
            toast.success('Delivery notifications disabled');
        }
    };

    // Filter packages by search query
    const filteredPackages = packages.filter(pkg => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            pkg.tracking_number.toLowerCase().includes(query) ||
            (pkg.description && pkg.description.toLowerCase().includes(query))
        );
    });

    // Sort packages
    const sortedPackages = [...filteredPackages].sort((a, b) => {
        switch (sortBy) {
            case 'date_asc':
                return new Date(a.created_at) - new Date(b.created_at);
            case 'date_desc':
                return new Date(b.created_at) - new Date(a.created_at);
            case 'status_asc':
                return (a.status || '').localeCompare(b.status || '');
            case 'status_desc':
                return (b.status || '').localeCompare(a.status || '');
            default:
                return 0;
        }
    });

    // Smart Groups sorting: UNKNOWN first, then PRE_TRANSIT, then rest by ETA
    const smartGroupedPackages = [...filteredPackages].sort((a, b) => {
        const statusPriority = (status) => {
            if (status === 'UNKNOWN') return 0;
            if (status === 'PRE_TRANSIT') return 1;
            return 2;
        };
        
        const priorityA = statusPriority(a.status);
        const priorityB = statusPriority(b.status);
        
        // First sort by status priority
        if (priorityA !== priorityB) {
            return priorityA - priorityB;
        }
        
        // For packages with same priority (both in "rest" category), sort by ETA
        if (priorityA === 2 && priorityB === 2) {
            // Handle null ETAs - push them to the end
            if (!a.eta && !b.eta) return 0;
            if (!a.eta) return 1;
            if (!b.eta) return -1;
            return new Date(a.eta) - new Date(b.eta);
        }
        
        // For UNKNOWN and PRE_TRANSIT, sort by date added (newest first)
        return new Date(b.created_at) - new Date(a.created_at);
    });

    const inboundPackages = sortedPackages.filter(p => p.direction === 'inbound');
    const outboundPackages = sortedPackages.filter(p => p.direction === 'outbound');

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" data-testid="dashboard-loading">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="min-h-screen noise-bg" data-testid="dashboard-page">
            {/* Test Mode Banner */}
            {settings.use_test_key && (
                <div className="bg-amber-500 text-amber-950 py-2 px-4 text-center text-sm font-medium" data-testid="test-mode-banner">
                    <span className="inline-flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        TEST MODE - Using test API key. Only 'shippo' carrier with test tracking numbers will work.
                        <Button 
                            variant="outline" 
                            size="sm" 
                            className="ml-2 h-6 bg-amber-600 border-amber-700 text-amber-50 hover:bg-amber-700 hover:text-white"
                            onClick={() => setSettingsDialogOpen(true)}
                        >
                            Switch to Live
                        </Button>
                    </span>
                </div>
            )}

            {/* Navbar */}
            <header className="sticky top-0 z-50 backdrop-blur-md bg-background/80 border-b border-border">
                <div className="flex justify-between items-center h-16 px-6 max-w-[1400px] mx-auto">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-zinc-900 dark:bg-zinc-100 rounded-sm">
                            <Package className="h-5 w-5 text-zinc-100 dark:text-zinc-900" strokeWidth={1.5} />
                        </div>
                        <span className="text-lg font-bold tracking-tight">Tracker</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Countdown Timer */}
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-sm text-sm" data-testid="countdown-timer">
                            <Timer className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                            {waitingForRefresh ? (
                                <span className="font-mono text-amber-600 dark:text-amber-400">
                                    Refreshing in {waitCountdown}s
                                </span>
                            ) : countdown !== null && countdown > 0 ? (
                                <span className="font-mono">
                                    {formatCountdown(countdown)}
                                </span>
                            ) : countdown === 0 ? (
                                <span className="font-mono text-green-600 dark:text-green-400 animate-pulse">
                                    Checking...
                                </span>
                            ) : (
                                <span className="font-mono text-muted-foreground">--:--</span>
                            )}
                        </div>
                        
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRefreshAll}
                            disabled={refreshing}
                            className="rounded-sm"
                            data-testid="refresh-all-button"
                        >
                            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} strokeWidth={1.5} />
                            Check Now
                        </Button>

                        {/* Debug Console Toggle */}
                        <Button
                            variant={debugLogs.some(l => l.type === 'error') ? "destructive" : "outline"}
                            size="icon"
                            onClick={() => setDebugConsoleOpen(!debugConsoleOpen)}
                            className="rounded-sm relative"
                            data-testid="debug-console-button"
                        >
                            <Terminal className="h-4 w-4" strokeWidth={1.5} />
                            {debugLogs.filter(l => l.type === 'error').length > 0 && (
                                <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center">
                                    {debugLogs.filter(l => l.type === 'error').length}
                                </span>
                            )}
                        </Button>

                        {/* Theme Toggle */}
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={toggleTheme}
                            className="rounded-sm"
                            data-testid="theme-toggle"
                        >
                            {theme === 'light' ? (
                                <Moon className="h-4 w-4" strokeWidth={1.5} />
                            ) : (
                                <Sun className="h-4 w-4" strokeWidth={1.5} />
                            )}
                        </Button>

                        {/* Settings Dialog */}
                        <Dialog open={settingsDialogOpen} onOpenChange={(open) => {
                            setSettingsDialogOpen(open);
                            if (!open) {
                                setConnectionStatus(null);
                                setNewApiKey('');
                            }
                        }}>
                            <DialogTrigger asChild>
                                <Button variant="outline" size="icon" className="rounded-sm" data-testid="settings-button">
                                    <Settings className="h-4 w-4" strokeWidth={1.5} />
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="rounded-sm max-w-md max-h-[90vh] overflow-y-auto overflow-x-hidden">
                                <DialogHeader>
                                    <DialogTitle className="text-xl font-semibold tracking-tight">Settings</DialogTitle>
                                    <DialogDescription className="text-sm text-muted-foreground">
                                        Configure your tracking dashboard preferences
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="py-4 space-y-6 overflow-hidden">
                                    {/* Auto-refresh Interval */}
                                    <div className="space-y-2">
                                        <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                            <Clock className="h-4 w-4" />
                                            Auto-refresh Interval
                                        </Label>
                                        <Select
                                            value={[180, 360, 720, 1440].includes(settings.update_frequency) ? settings.update_frequency.toString() : '1440'}
                                            onValueChange={(val) => handleUpdateSettings(parseInt(val))}
                                        >
                                            <SelectTrigger className="rounded-sm" data-testid="frequency-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="180">Every 3 hours</SelectItem>
                                                <SelectItem value="360">Every 6 hours</SelectItem>
                                                <SelectItem value="720">Every 12 hours</SelectItem>
                                                <SelectItem value="1440">Every 24 hours</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Delivery Notifications */}
                                    <div className="space-y-2">
                                        <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                            <Bell className="h-4 w-4" />
                                            Delivery Notifications
                                        </Label>
                                        <div className="flex items-center justify-between p-3 border border-border rounded-sm">
                                            <div>
                                                <p className="text-sm font-medium">Browser Notifications</p>
                                                <p className="text-xs text-muted-foreground">Get notified when packages are delivered</p>
                                            </div>
                                            <Switch
                                                checked={notificationsEnabled}
                                                onCheckedChange={handleToggleNotifications}
                                                data-testid="notifications-toggle"
                                            />
                                        </div>
                                        {!checkNotificationSupport() && (
                                            <p className="text-xs text-destructive">Your browser doesn't support notifications</p>
                                        )}
                                    </div>

                                    {/* Shippo API Key */}
                                    <div className="space-y-3">
                                        <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                            <Key className="h-4 w-4" />
                                            Shippo API Key
                                        </Label>
                                        
                                        {/* Key Mode Toggle */}
                                        <div className="flex items-center justify-between p-3 border border-border rounded-sm bg-muted/30">
                                            <div className="flex items-center gap-3">
                                                <div className={`px-2 py-1 rounded text-xs font-bold ${settings.use_test_key ? 'bg-amber-500 text-amber-950' : 'bg-green-500 text-green-950'}`}>
                                                    {settings.use_test_key ? 'TEST' : 'LIVE'}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium">
                                                        {settings.use_test_key ? 'Test Mode' : 'Live Mode'}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {settings.use_test_key 
                                                            ? 'Only shippo carrier works' 
                                                            : 'All carriers supported'}
                                                    </p>
                                                </div>
                                            </div>
                                            <Switch
                                                checked={!settings.use_test_key}
                                                onCheckedChange={(checked) => handleToggleKeyMode(!checked)}
                                                disabled={togglingKeyMode || (!settings.key_mode?.has_live_key && settings.use_test_key)}
                                                data-testid="key-mode-toggle"
                                            />
                                        </div>

                                        {/* Live API Key Input */}
                                        <div className="space-y-2">
                                            <p className="text-xs text-muted-foreground">
                                                {settings.key_mode?.has_live_key 
                                                    ? 'Live key saved. Enter a new key to replace it.'
                                                    : 'Enter your Shippo live API key (shippo_live_...)'}
                                            </p>
                                            {settings.shippo_api_key && (
                                                <div className="text-sm font-mono bg-muted px-3 py-2 rounded-sm overflow-hidden">
                                                    <span className="text-muted-foreground">Saved key: </span>
                                                    <span className="break-all">{settings.shippo_api_key}</span>
                                                </div>
                                            )}
                                            <div className="flex gap-2">
                                                <Input
                                                    type="password"
                                                    value={newApiKey}
                                                    onChange={(e) => setNewApiKey(e.target.value)}
                                                    placeholder="shippo_live_..."
                                                    className="rounded-sm font-mono text-sm min-w-0 flex-1"
                                                    data-testid="api-key-input"
                                                />
                                                <Button
                                                    onClick={handleSaveApiKey}
                                                    disabled={savingApiKey || !newApiKey.trim()}
                                                    className="rounded-sm flex-shrink-0"
                                                    data-testid="save-api-key-button"
                                                >
                                                    {savingApiKey ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                                                </Button>
                                            </div>
                                        </div>
                                        
                                        {/* Test Connection Button */}
                                        <Button
                                            variant="outline"
                                            onClick={handleTestConnection}
                                            disabled={testingConnection}
                                            className="w-full rounded-sm"
                                            data-testid="test-connection-button"
                                        >
                                            {testingConnection ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                    Testing...
                                                </>
                                            ) : (
                                                <>
                                                    <Zap className="h-4 w-4 mr-2" />
                                                    Test API Connection
                                                </>
                                            )}
                                        </Button>

                                        {/* Connection Status */}
                                        {connectionStatus && (
                                            <Alert className={`rounded-sm overflow-hidden ${connectionStatus.success ? 'border-green-500 bg-green-50 dark:bg-green-950/20' : 'border-red-500 bg-red-50 dark:bg-red-950/20'}`}>
                                                <div className="flex items-start gap-2 min-w-0">
                                                    {connectionStatus.success ? (
                                                        <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                                                    ) : (
                                                        <XCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                                                    )}
                                                    <AlertDescription className="text-sm min-w-0 overflow-hidden">
                                                        <p className="font-medium break-words">{connectionStatus.message}</p>
                                                        {connectionStatus.api_type && (
                                                            <p className="text-xs mt-1">
                                                                API Type: <span className="font-semibold">{connectionStatus.api_type.toUpperCase()}</span>
                                                            </p>
                                                        )}
                                                        {connectionStatus.note && (
                                                            <p className="text-xs mt-1 text-muted-foreground break-words">{connectionStatus.note}</p>
                                                        )}
                                                    </AlertDescription>
                                                </div>
                                            </Alert>
                                        )}

                                        {/* API Key Info */}
                                        <Alert className="rounded-sm border-amber-500 bg-amber-50 dark:bg-amber-950/20 overflow-hidden">
                                            <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                                            <AlertDescription className="text-xs min-w-0">
                                                <p className="font-medium">Important:</p>
                                                <ul className="list-disc list-inside mt-1 space-y-0.5">
                                                    <li className="break-words"><strong>Test keys</strong> (shippo_test_...) only work with carrier "shippo" and test tracking numbers</li>
                                                    <li className="break-words"><strong>Live keys</strong> (shippo_live_...) required for real carriers (USPS, FedEx, UPS)</li>
                                                </ul>
                                            </AlertDescription>
                                        </Alert>
                                    </div>

                                    {/* Refresh Delay Setting */}
                                    <div className="space-y-2">
                                        <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                            <Timer className="h-4 w-4" />
                                            UI Refresh Delay
                                        </Label>
                                        <p className="text-xs text-muted-foreground">
                                            Time to wait after auto-check before refreshing the UI
                                        </p>
                                        <Select
                                            value={settings.refresh_delay?.toString() || '10'}
                                            onValueChange={async (val) => {
                                                const updated = await updateSettings({ 
                                                    ...settings, 
                                                    refresh_delay: parseInt(val)
                                                });
                                                setSettings(updated);
                                                toast.success(`Refresh delay set to ${val} seconds`);
                                            }}
                                        >
                                            <SelectTrigger className="rounded-sm" data-testid="refresh-delay-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="10">10 seconds</SelectItem>
                                                <SelectItem value="30">30 seconds</SelectItem>
                                                <SelectItem value="60">60 seconds</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Chime Sound Setting */}
                                    <div className="space-y-2">
                                        <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                            {settings.chime_enabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                                            Update Sound
                                        </Label>
                                        <div className="flex items-center justify-between p-3 border border-border rounded-sm">
                                            <div>
                                                <p className="text-sm font-medium">Chime on Updates</p>
                                                <p className="text-xs text-muted-foreground">Play a sound when packages are updated</p>
                                            </div>
                                            <Switch
                                                checked={settings.chime_enabled}
                                                onCheckedChange={async (checked) => {
                                                    const updated = await updateSettings({ 
                                                        ...settings, 
                                                        chime_enabled: checked
                                                    });
                                                    setSettings(updated);
                                                    toast.success(checked ? 'Update sound enabled' : 'Update sound disabled');
                                                }}
                                                data-testid="chime-toggle"
                                            />
                                        </div>
                                    </div>

                                    {/* Change Password */}
                                    <div className="space-y-3 pt-4 border-t border-border">
                                        <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                            <Key className="h-4 w-4" />
                                            Change Password
                                        </Label>
                                        <div className="space-y-2">
                                            <Input
                                                type="password"
                                                value={currentPassword}
                                                onChange={(e) => setCurrentPassword(e.target.value)}
                                                placeholder="Current password"
                                                className="rounded-sm text-sm"
                                                data-testid="current-password-input"
                                            />
                                            <Input
                                                type="password"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                placeholder="New password (min 6 characters)"
                                                className="rounded-sm text-sm"
                                                data-testid="new-password-input"
                                            />
                                            <Input
                                                type="password"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                placeholder="Confirm new password"
                                                className="rounded-sm text-sm"
                                                data-testid="confirm-password-input"
                                            />
                                            <Button
                                                onClick={handleChangePassword}
                                                disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                                                className="w-full rounded-sm"
                                                variant="outline"
                                                data-testid="change-password-button"
                                            >
                                                {changingPassword ? (
                                                    <>
                                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                        Changing...
                                                    </>
                                                ) : (
                                                    'Change Password'
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>

                        {/* Export Button */}
                        <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
                            <DialogTrigger asChild>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="rounded-sm"
                                    data-testid="export-button"
                                >
                                    <Download className="h-4 w-4 mr-2" strokeWidth={1.5} />
                                    Export
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="rounded-sm max-w-lg">
                                <DialogHeader>
                                    <DialogTitle className="text-xl font-semibold tracking-tight">Export Tracking Numbers</DialogTitle>
                                    <DialogDescription className="text-sm text-muted-foreground">
                                        Copy all non-delivered tracking numbers for easy export
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4 pt-2">
                                    {(() => {
                                        const nonDelivered = packages.filter(p => p.status !== 'DELIVERED');
                                        const trackingList = nonDelivered.map(p => p.tracking_number).join('\n');
                                        return (
                                            <>
                                                <div className="flex items-center justify-between text-sm text-muted-foreground">
                                                    <span>{nonDelivered.length} non-delivered package{nonDelivered.length !== 1 ? 's' : ''}</span>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="rounded-sm"
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(trackingList);
                                                            toast.success('Tracking numbers copied to clipboard!');
                                                        }}
                                                        disabled={nonDelivered.length === 0}
                                                    >
                                                        <Copy className="h-4 w-4 mr-2" strokeWidth={1.5} />
                                                        Copy All
                                                    </Button>
                                                </div>
                                                <ScrollArea className="h-[300px] rounded-sm border border-border">
                                                    <div className="p-3 font-mono text-sm whitespace-pre-wrap break-all">
                                                        {trackingList || <span className="text-muted-foreground italic">No non-delivered packages</span>}
                                                    </div>
                                                </ScrollArea>
                                            </>
                                        );
                                    })()}
                                </div>
                                <DialogFooter>
                                    <DialogClose asChild>
                                        <Button variant="outline" className="rounded-sm">Close</Button>
                                    </DialogClose>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>

                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate('/archive')}
                            className="rounded-sm"
                            data-testid="archive-nav-button"
                        >
                            <Archive className="h-4 w-4 mr-2" strokeWidth={1.5} />
                            Archive
                        </Button>

                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleLogout}
                            className="rounded-sm"
                            data-testid="logout-button"
                        >
                            <LogOut className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="p-6 md:p-12 max-w-[1400px] mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">Dashboard</h1>
                        <p className="text-muted-foreground text-sm">
                            {packages.length} active package{packages.length !== 1 ? 's' : ''} • Auto-refresh every {settings.update_frequency / 60}h
                        </p>
                    </div>

                    {/* Add Package Dialog */}
                    <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="rounded-sm font-medium" data-testid="add-package-button">
                                <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
                                Add Package
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-sm">
                            <DialogHeader>
                                <DialogTitle className="text-xl font-semibold tracking-tight">Add Package</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleAddPackage} className="space-y-4 pt-4">
                                {/* Bulk Mode Toggle */}
                                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-sm">
                                    <div className="flex items-center gap-2">
                                        <Label htmlFor="bulk-mode" className="text-sm font-medium cursor-pointer">
                                            Bulk Import
                                        </Label>
                                        <span className="text-xs text-muted-foreground">
                                            (multiple tracking numbers)
                                        </span>
                                    </div>
                                    <Switch
                                        id="bulk-mode"
                                        checked={bulkMode}
                                        onCheckedChange={setBulkMode}
                                    />
                                </div>

                                {bulkMode ? (
                                    /* Bulk Mode - Textarea for multiple tracking numbers with optional descriptions */
                                    <div className="space-y-2">
                                        <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                                            Tracking Numbers * (one per line, optional description after comma)
                                        </Label>
                                        <Textarea
                                            value={bulkTrackingNumbers}
                                            onChange={(e) => setBulkTrackingNumbers(e.target.value)}
                                            placeholder={"1Z999AA10123456784, Amazon order #12345\n9400111899223033005019, Mom's birthday gift\n784644583224"}
                                            className="rounded-sm font-mono text-sm"
                                            rows={6}
                                            data-testid="bulk-tracking-input"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            {bulkTrackingNumbers.split('\n').filter(t => t.trim()).length} item(s) entered • Format: tracking_number, description
                                        </p>
                                    </div>
                                ) : (
                                    /* Single Mode - Original input */
                                    <div className="space-y-2">
                                    <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                                        Tracking Number *
                                    </Label>
                                    <Input
                                        value={newPackage.tracking_number}
                                        onChange={(e) => setNewPackage({ ...newPackage, tracking_number: e.target.value })}
                                        placeholder="1Z999AA10123456784"
                                        className="rounded-sm font-mono"
                                        data-testid="tracking-number-input"
                                    />
                                    </div>
                                )}
                                <div className="space-y-2">
                                    <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                                        Carrier *
                                    </Label>
                                    <Select
                                        value={newPackage.carrier}
                                        onValueChange={(val) => setNewPackage({ ...newPackage, carrier: val })}
                                    >
                                        <SelectTrigger className="rounded-sm" data-testid="carrier-select">
                                            <SelectValue placeholder="Select carrier" />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-[300px]">
                                            {carriers.map((c) => (
                                                <SelectItem key={c.token} value={c.token}>
                                                    <span className="flex items-center gap-2">
                                                        <CarrierLogo carrier={c.token} carrierName={c.name} size="sm" />
                                                        {c.name}
                                                    </span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                                        Direction *
                                    </Label>
                                    <Select
                                        value={newPackage.direction}
                                        onValueChange={(val) => setNewPackage({ ...newPackage, direction: val })}
                                    >
                                        <SelectTrigger className="rounded-sm" data-testid="direction-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="inbound">
                                                <span className="flex items-center gap-2">
                                                    <ArrowDownLeft className="h-4 w-4 text-purple-600" />
                                                    Inbound
                                                </span>
                                            </SelectItem>
                                            <SelectItem value="outbound">
                                                <span className="flex items-center gap-2">
                                                    <ArrowUpRight className="h-4 w-4 text-blue-600" />
                                                    Outbound
                                                </span>
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                {/* Description field - only show in single mode */}
                                {!bulkMode && (
                                    <div className="space-y-2">
                                        <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                                            Description (optional)
                                        </Label>
                                        <Textarea
                                            value={newPackage.description}
                                            onChange={(e) => setNewPackage({ ...newPackage, description: e.target.value })}
                                            placeholder="What's in this package?"
                                            className="rounded-sm resize-none"
                                            rows={2}
                                            data-testid="description-input"
                                        />
                                    </div>
                                )}
                                <DialogFooter>
                                    <DialogClose asChild>
                                        <Button type="button" variant="outline" className="rounded-sm">Cancel</Button>
                                    </DialogClose>
                                    <Button type="submit" className="rounded-sm" disabled={addingPackage} data-testid="submit-package-button">
                                        {addingPackage ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                {bulkMode ? 'Adding Packages...' : 'Adding...'}
                                            </>
                                        ) : (
                                            bulkMode ? `Add ${bulkTrackingNumbers.split('\n').filter(t => t.trim()).length || ''} Package${bulkTrackingNumbers.split('\n').filter(t => t.trim()).length !== 1 ? 's' : ''}` : 'Add Package'
                                        )}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Search and Sort Controls */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                        <Input
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search tracking # or description..."
                            className="pl-10 rounded-sm"
                            data-testid="search-input"
                        />
                    </div>
                    <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger className="w-[200px] rounded-sm" data-testid="sort-select">
                            <ArrowUpDown className="h-4 w-4 mr-2" strokeWidth={1.5} />
                            <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="date_desc">Newest First</SelectItem>
                            <SelectItem value="date_asc">Oldest First</SelectItem>
                            <SelectItem value="status_asc">Status A-Z</SelectItem>
                            <SelectItem value="status_desc">Status Z-A</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Tabs for Inbound/Outbound */}
                <Tabs defaultValue="all" className="w-full">
                    <TabsList className="rounded-sm mb-6" data-testid="package-tabs">
                        <TabsTrigger value="all" className="rounded-sm">
                            All ({sortedPackages.length})
                        </TabsTrigger>
                        <TabsTrigger value="smart" className="rounded-sm">
                            <Sparkles className="h-4 w-4 mr-2 text-amber-500" />
                            Smart Groups
                        </TabsTrigger>
                        <TabsTrigger value="inbound" className="rounded-sm">
                            <ArrowDownLeft className="h-4 w-4 mr-2 text-purple-600" />
                            Inbound ({inboundPackages.length})
                        </TabsTrigger>
                        <TabsTrigger value="outbound" className="rounded-sm">
                            <ArrowUpRight className="h-4 w-4 mr-2 text-blue-600" />
                            Outbound ({outboundPackages.length})
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="all" className="mt-0">
                        <PackageGrid packages={sortedPackages} onDelete={handleDelete} onArchive={handleArchive} onRefresh={handleRefreshSingle} onEdit={handleOpenEdit} searchQuery={searchQuery} updatedPackageIds={updatedPackageIds} />
                    </TabsContent>
                    <TabsContent value="smart" className="mt-0">
                        <div className="mb-4 p-3 bg-muted/50 rounded-sm text-sm text-muted-foreground">
                            <Sparkles className="h-4 w-4 inline mr-2 text-amber-500" />
                            Showing: Unknown status → Pre-Transit → Rest by ETA (soonest first)
                        </div>
                        <PackageGrid packages={smartGroupedPackages} onDelete={handleDelete} onArchive={handleArchive} onRefresh={handleRefreshSingle} onEdit={handleOpenEdit} searchQuery={searchQuery} updatedPackageIds={updatedPackageIds} />
                    </TabsContent>
                    <TabsContent value="inbound" className="mt-0">
                        <PackageGrid packages={inboundPackages} onDelete={handleDelete} onArchive={handleArchive} onRefresh={handleRefreshSingle} onEdit={handleOpenEdit} searchQuery={searchQuery} updatedPackageIds={updatedPackageIds} />
                    </TabsContent>
                    <TabsContent value="outbound" className="mt-0">
                        <PackageGrid packages={outboundPackages} onDelete={handleDelete} onArchive={handleArchive} onRefresh={handleRefreshSingle} onEdit={handleOpenEdit} searchQuery={searchQuery} updatedPackageIds={updatedPackageIds} />
                    </TabsContent>
                </Tabs>

                {/* Edit Package Dialog */}
                <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                    <DialogContent className="rounded-sm">
                        <DialogHeader>
                            <DialogTitle className="text-xl font-semibold tracking-tight">Edit Package</DialogTitle>
                            <DialogDescription className="text-sm text-muted-foreground">
                                Update package details. {editingPackage?.status === 'DELIVERED' && '(Tracking number cannot be changed for delivered packages)'}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                            <div className="space-y-2">
                                <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                                    Tracking Number
                                </Label>
                                <Input
                                    value={editForm.tracking_number}
                                    onChange={(e) => setEditForm({ ...editForm, tracking_number: e.target.value })}
                                    disabled={editingPackage?.status === 'DELIVERED'}
                                    className="rounded-sm font-mono"
                                    data-testid="edit-tracking-input"
                                />
                                {editingPackage?.status === 'DELIVERED' && (
                                    <p className="text-xs text-muted-foreground">Cannot change tracking number for delivered packages</p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                                    Direction
                                </Label>
                                <Select
                                    value={editForm.direction}
                                    onValueChange={(val) => setEditForm({ ...editForm, direction: val })}
                                >
                                    <SelectTrigger className="rounded-sm" data-testid="edit-direction-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="inbound">
                                            <span className="flex items-center gap-2">
                                                <ArrowDownLeft className="h-4 w-4 text-purple-600" />
                                                Inbound
                                            </span>
                                        </SelectItem>
                                        <SelectItem value="outbound">
                                            <span className="flex items-center gap-2">
                                                <ArrowUpRight className="h-4 w-4 text-blue-600" />
                                                Outbound
                                            </span>
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                                    Description / Notes
                                </Label>
                                <Textarea
                                    value={editForm.description}
                                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                    placeholder="What's in this package?"
                                    className="rounded-sm resize-none"
                                    rows={2}
                                    data-testid="edit-description-input"
                                />
                            </div>
                        </div>
                        <DialogFooter className="mt-4">
                            <DialogClose asChild>
                                <Button type="button" variant="outline" className="rounded-sm">Cancel</Button>
                            </DialogClose>
                            <Button onClick={handleSaveEdit} className="rounded-sm" disabled={savingEdit} data-testid="save-edit-button">
                                {savingEdit ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    'Save Changes'
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </main>

            {/* Debug Console Panel */}
            {debugConsoleOpen && (
                <div className="fixed bottom-0 left-0 right-0 z-50 bg-zinc-900 text-zinc-100 border-t border-zinc-700 shadow-2xl" data-testid="debug-console">
                    <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-700 bg-zinc-800">
                        <div className="flex items-center gap-2">
                            <Terminal className="h-4 w-4" />
                            <span className="font-semibold text-sm">API Debug Console</span>
                            <Badge variant="outline" className="text-xs border-zinc-600 text-zinc-400">
                                {debugLogs.length} logs
                            </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDebugLogs([])}
                                className="text-zinc-400 hover:text-zinc-100 h-7 text-xs"
                            >
                                Clear
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDebugConsoleOpen(false)}
                                className="text-zinc-400 hover:text-zinc-100 h-7 w-7"
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                    <ScrollArea className="h-64">
                        <div className="p-2 font-mono text-xs space-y-1">
                            {debugLogs.length === 0 ? (
                                <div className="text-zinc-500 p-4 text-center">
                                    No logs yet. Click "Check Now" to refresh packages and see API responses.
                                </div>
                            ) : (
                                debugLogs.map(log => (
                                    <div 
                                        key={log.id} 
                                        className={`p-2 rounded ${
                                            log.type === 'error' ? 'bg-red-950/50 text-red-300' :
                                            log.type === 'success' ? 'bg-green-950/50 text-green-300' :
                                            'bg-zinc-800/50 text-zinc-300'
                                        }`}
                                    >
                                        <div className="flex items-start gap-2">
                                            <span className="text-zinc-500 flex-shrink-0">
                                                {new Date(log.timestamp).toLocaleTimeString()}
                                            </span>
                                            <span className="flex-1">{log.message}</span>
                                        </div>
                                        {log.data && (
                                            <details className="mt-1 ml-16">
                                                <summary className="text-zinc-500 cursor-pointer hover:text-zinc-300">
                                                    View details
                                                </summary>
                                                <pre className="mt-1 p-2 bg-zinc-950 rounded text-[10px] overflow-x-auto whitespace-pre-wrap">
                                                    {JSON.stringify(log.data, null, 2)}
                                                </pre>
                                            </details>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </ScrollArea>
                </div>
            )}
        </div>
    );
}

// Carrier Logo Component
function CarrierLogo({ carrier, carrierName, size = "md" }) {
    const [imageError, setImageError] = useState(false);
    const color = getCarrierColor(carrier);
    const initials = getCarrierInitials(carrierName);
    
    const sizeClasses = {
        sm: "h-5 w-5 text-[8px]",
        md: "h-8 w-8 text-xs",
        lg: "h-10 w-10 text-sm"
    };

    // Always use initials-based logo for reliability
    return (
        <div 
            className={`${sizeClasses[size]} rounded-sm flex items-center justify-center font-bold text-white flex-shrink-0`}
            style={{ backgroundColor: color }}
            title={carrierName}
        >
            {initials}
        </div>
    );
}

function PackageGrid({ packages, onDelete, onArchive, onRefresh, onEdit, searchQuery, updatedPackageIds = new Set() }) {
    if (packages.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-state">
                <div className="relative w-48 h-48 mb-6">
                    <img
                        src="https://images.unsplash.com/photo-1624137527136-66e631bdaa0e?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"
                        alt="No packages"
                        className="w-full h-full object-cover rounded-sm empty-state-image"
                    />
                </div>
                <h3 className="text-lg font-semibold mb-2">
                    {searchQuery ? 'No matching packages' : 'No packages yet'}
                </h3>
                <p className="text-muted-foreground text-sm max-w-sm">
                    {searchQuery 
                        ? 'Try adjusting your search query to find what you\'re looking for.'
                        : 'Add a package to start tracking. Enter your tracking number and select the carrier.'}
                </p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="package-grid">
            {packages.map((pkg, index) => (
                <PackageCard
                    key={pkg.id}
                    pkg={pkg}
                    onDelete={onDelete}
                    onArchive={onArchive}
                    onRefresh={onRefresh}
                    onEdit={onEdit}
                    style={{ animationDelay: `${index * 50}ms` }}
                    isUpdated={updatedPackageIds.has(pkg.id)}
                />
            ))}
        </div>
    );
}

function PackageCard({ pkg, onDelete, onArchive, onRefresh, onEdit, style, isUpdated = false }) {
    const [refreshing, setRefreshing] = useState(false);

    const handleRefresh = async () => {
        setRefreshing(true);
        await onRefresh(pkg.id);
        setRefreshing(false);
    };

    return (
        <div 
            className={`card-flip-container ${isUpdated ? 'animate-flip' : ''}`}
            style={style}
        >
            <Card
                className={`package-card border border-border rounded-sm shadow-none ${!isUpdated ? 'animate-slide-up' : ''} ${isUpdated ? 'ring-2 ring-green-500 ring-offset-2 dark:ring-offset-zinc-900' : ''}`}
                data-testid={`package-card-${pkg.id}`}
            >
            <CardHeader className="p-4 pb-2">
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                        <Badge className={`${getDirectionClass(pkg.direction)} rounded-full text-xs font-semibold`}>
                            {pkg.direction === 'inbound' ? (
                                <ArrowDownLeft className="h-3 w-3 mr-1" />
                            ) : (
                                <ArrowUpRight className="h-3 w-3 mr-1" />
                            )}
                            {pkg.direction}
                        </Badge>
                        <Badge className={`${getStatusClass(pkg.status)} rounded-full text-xs font-semibold`}>
                            {formatStatus(pkg.status)}
                        </Badge>
                    </div>
                    <div className="flex items-center gap-1">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-sm"
                            onClick={handleRefresh}
                            disabled={refreshing}
                            data-testid={`refresh-btn-${pkg.id}`}
                        >
                            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} strokeWidth={1.5} />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-sm"
                            onClick={() => onEdit(pkg)}
                            data-testid={`edit-btn-${pkg.id}`}
                        >
                            <Pencil className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-sm"
                            onClick={() => onArchive(pkg.id)}
                            data-testid={`archive-btn-${pkg.id}`}
                        >
                            <Archive className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-sm text-destructive hover:text-destructive"
                            onClick={() => onDelete(pkg.id)}
                            data-testid={`delete-btn-${pkg.id}`}
                        >
                            <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="p-4 pt-2 space-y-3">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">Tracking #</p>
                    <p className="font-mono font-medium tracking-wide text-sm" data-testid={`tracking-number-${pkg.id}`}>
                        {pkg.tracking_number}
                    </p>
                </div>

                <div className="flex items-center gap-3 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                        <CarrierLogo carrier={pkg.carrier} carrierName={pkg.carrier_name} size="md" />
                        <span>{pkg.carrier_name}</span>
                    </div>
                    {pkg.eta && (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                            <Clock className="h-4 w-4" strokeWidth={1.5} />
                            <span>ETA: {formatDate(pkg.eta).split(',')[0]}</span>
                        </div>
                    )}
                </div>

                {pkg.status_details && (
                    <p className="text-sm text-muted-foreground line-clamp-2">{pkg.status_details}</p>
                )}

                {pkg.description && (
                    <p className="text-sm text-muted-foreground italic">"{pkg.description}"</p>
                )}

                {pkg.tracking_history && pkg.tracking_history.length > 0 && (
                    <div className="pt-2 border-t border-border">
                        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Latest Update</p>
                        <div className="flex items-start gap-2 text-sm">
                            <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                            <div>
                                <p className="font-medium">{pkg.tracking_history[0].status_details}</p>
                                {pkg.tracking_history[0].location && (
                                    <p className="text-muted-foreground text-xs">{pkg.tracking_history[0].location}</p>
                                )}
                                <p className="text-muted-foreground text-xs">{formatDate(pkg.tracking_history[0].status_date)}</p>
                            </div>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
        </div>
    );
}
